import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, effect, inject, input, output, signal,
  untracked, viewChild
} from '@angular/core';
import {QrCodeComponent} from '../../../_ui/components/qr-code/qr-code.component';
import {DeviceEnrollmentSession, ENROLLMENT_SESSION_LIFETIME_SECONDS} from '../shared/device-enrollment.models';
import {
  DEFAULT_WINDOW_SECONDS, RotatingEnrollmentCodeService, enrollmentPayload, msUntilNextWindow, windowIndexAt
} from '../shared/rotating-enrollment-code';

/**
 * How far ahead of the session's backstop the panel asks its parent for a fresh session, in milliseconds.
 * Long enough that a round-trip lands before the old session dies (so the operator never sees a dead code
 * on the way to the till), short enough that a refused renewal still leaves the old code live for a while.
 */
export const ENROLLMENT_RENEW_LEAD_MS = 30_000;

/**
 * The register-enrolment QR, rotating (#469, step 5; rotating and account-free since #279).
 *
 * ### What this screen has to get right
 *
 * **The code never goes stale while the session lives.** The server returns a session id and a shared secret,
 * not a code; this panel derives the rotating value for the current window locally (`RotatingEnrollmentCodeService`,
 * HMAC-SHA-256 over the window counter — see `rotating-enrollment-code.ts`), builds the `{host, code}` payload
 * and renders the QR itself. At each window boundary it recomputes and re-renders, so there is no "expired"
 * state to walk into with the till in hand — the server also accepts the previous window, so a scan straddling
 * the boundary still lands.
 *
 * **The QR carries the server address as well as the code.** A fresh iPad does not know which host to talk to,
 * and typing one on a device with no keyboard is the step this feature exists to remove. The address is
 * therefore also shown as text, because it is the one thing about a QR that nobody can check by looking at it.
 *
 * **The paste fallback carries the whole payload.** The register accepts exactly what its camera would have
 * decoded and nothing looser (`DeviceEnrollmentPayload.TryParse` refuses a bare code), so the field the
 * operator copies from holds the payload — the JSON with host and code — not the digits alone.
 *
 * **The secret lives here and nowhere else.** It is an input held in memory for as long as the panel is open.
 * It is not written to `localStorage` (trapped by `CookieService` anyway), not to a store, and not logged;
 * closing the tab is what forgets it.
 *
 * ### The bar drains, it does not tick
 *
 * The countdown is one linear Web Animations run per window, from the fraction of the window that is left to
 * empty, over exactly the milliseconds that are left — no per-second timer, no stepping. It restarts at every
 * rotation. It is decorative for assistive technology (`aria-hidden`): a value that changed every second would
 * be unusable in a screen reader, and the fact that matters — a code is on screen and refreshes itself — is
 * announced once, as a sentence.
 *
 * ### The backstop, and the clock that is not ours
 *
 * A session is redeemable until the server's `expiration` (about ten minutes). {@link ENROLLMENT_RENEW_LEAD_MS}
 * before it the panel emits `renew`, and the parent silently opens a fresh session (bounded; see
 * `DeviceEnrollmentComponent`) — the operator sees the code change and nothing else. Only when no renewal
 * arrives does the panel reach its lapsed state, with *Neuen Code anzeigen* one press away.
 *
 * `expiration` is stamped on the **server's** clock. A reading that is not plausibly a live session — already
 * past, or more than a day out — says more about this machine's clock than about the session, and is replaced
 * by the documented lifetime: presenting a live session as dead would loop the operator through renewals that
 * were never needed. The server is always the authority; it refuses a stale code regardless.
 */
@Component({
  selector: 'device-enrollment-code-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [QrCodeComponent],
  template: `
    <section class="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6"
             aria-labelledby="enrollment-code-heading">
      <div class="flex flex-col gap-6 lg:flex-row lg:items-start">

        <!-- The code itself. Dimmed once the session has lapsed, so the screen never presents a dead code as
             usable. Until the first window's code has been computed (a few milliseconds) the frame is drawn
             empty rather than with a placeholder QR that would scan to nothing. -->
        <div class="mx-auto w-full max-w-[16rem] shrink-0 lg:mx-0">
          <div class="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm transition-all duration-200"
               [class.opacity-30]="expired()" [class.grayscale]="expired()">
            @if (payload(); as value) {
              <cqme-qr-code [value]="value" [size]="232" ariaLabel="QR-Code zur Einrichtung dieser Kasse"/>
            } @else {
              <div class="aspect-square w-full" aria-hidden="true"></div>
            }
          </div>

          @if (expired()) {
            <p class="mt-2 text-center text-xs font-semibold text-red-700">Nicht mehr gültig</p>
          }
        </div>

        <div class="min-w-0 flex-1 space-y-4">
          <div>
            <h2 id="enrollment-code-heading" class="text-xl font-bold tracking-tight text-gray-900">
              QR-Code scannen
            </h2>
            <!-- Since #279 a code names no account, only the Standort it was issued for — so the
                 subtitle states the scope instead of an account name. -->
            <p class="mt-1 text-sm text-gray-500">Gilt für diesen Standort</p>
          </div>

          <!-- The draining bar. Decorative for AT (see the class comment); the state is announced once below. -->
          <div>
            <div class="flex items-baseline justify-between gap-2">
              <span class="text-sm font-bold tracking-tight text-gray-700">
                {{ expired() ? 'Abgelaufen' : 'Nächster Code' }}
              </span>
              @if (!expired()) {
                <span class="text-xs text-gray-500">erneuert sich automatisch</span>
              }
            </div>
            <div class="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
              <div #bar id="enrollment-window-bar" class="h-full w-0 rounded-full"
                   [class.bg-theme-primary-600]="!expired()" [class.bg-red-600]="expired()"></div>
            </div>
            <p class="sr-only" role="status" aria-live="polite">{{ liveStatus() }}</p>
          </div>

          @if (expired()) {
            <div class="rounded-2xl border border-red-100 bg-red-50 p-4" role="alert">
              <p class="text-sm font-bold text-red-800">Dieser Code ist abgelaufen</p>
              <p class="mt-1 text-sm text-red-800">
                Lassen Sie einen neuen anzeigen und scannen Sie ihn direkt am Gerät.
              </p>
            </div>
          } @else {
            <div class="rounded-2xl border border-amber-100 bg-amber-50 p-4" role="note">
              <p class="text-sm font-bold text-amber-800">Der Code gilt nur in diesem Fenster</p>
              <p class="mt-1 text-sm text-amber-800">
                Er erneuert sich von selbst, solange die Seite geöffnet ist. Scannen Sie ihn am Gerät und
                schließen Sie die Seite, sobald die Kasse angemeldet ist.
              </p>
            </div>
          }

          <div>
            <label for="enrollment-host" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Server-Adresse im Code
            </label>
            <input id="enrollment-host" type="text" readonly [value]="session().host"
                   class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 font-mono text-xs
                          text-gray-700 shadow-sm transition-all duration-200
                          focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500">
            <p class="mt-2 text-xs text-gray-500">
              Das Gerät verbindet sich mit dieser Adresse. Prüfen Sie sie, bevor Sie den Code scannen.
            </p>
          </div>

          <div>
            <label for="enrollment-code" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Code zum Einfügen am Gerät
            </label>
            <div class="flex flex-col gap-2 sm:flex-row">
              <input id="enrollment-code" #codeField type="text" readonly [value]="payload() ?? ''"
                     class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 font-mono
                            text-xs font-semibold text-gray-900 shadow-sm transition-all duration-200
                            focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500">
              <button type="button" (click)="copyCode()" [disabled]="!payload()"
                      class="inline-flex shrink-0 items-center justify-center rounded-2xl border border-gray-200
                             px-4 py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all
                             duration-200 hover:border-gray-300 hover:bg-gray-50 active:scale-95
                             disabled:cursor-not-allowed disabled:opacity-30">
                Code kopieren
              </button>
            </div>
            <p class="mt-2 text-xs text-gray-500">
              Kann die Kasse keinen QR-Code scannen, richten Sie sie über den Reiter „Zugangsdaten“ mit Client-ID und Passwort ein.
            </p>
          </div>

          @if (feedback()) {
            <p class="text-sm font-semibold" role="status" aria-live="polite"
               [class.text-green-700]="copied()" [class.text-red-700]="!copied()">{{ feedback() }}</p>
          }

          <div class="flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
            <button type="button" (click)="reissue.emit()" [disabled]="busy()"
                    class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5 py-2.5
                           text-sm font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all
                           duration-200 hover:bg-theme-primary-700 hover:shadow-xl active:scale-95
                           disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale">
              Neuen Code anzeigen
            </button>
          </div>
        </div>
      </div>
    </section>
  `
})
export class EnrollmentCodePanelComponent {
  /** The opened session exactly as the server returned it — the only thing that carries the secret. */
  readonly session = input.required<DeviceEnrollmentSession>();

  /** True while a (re-)issue is in flight, so the button cannot be pressed twice. */
  readonly busy = input<boolean>(false);

  /** The operator asked for a fresh session. The parent issues it; this component never calls the API. */
  readonly reissue = output<void>();

  /**
   * The session's backstop is {@link ENROLLMENT_RENEW_LEAD_MS} away. Emitted once per session; the parent
   * decides whether to open a fresh one silently (it does, within a bound) — the panel only reports.
   */
  readonly renew = output<void>();

  private readonly codes = inject(RotatingEnrollmentCodeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly codeField = viewChild<ElementRef<HTMLInputElement>>('codeField');
  private readonly bar = viewChild<ElementRef<HTMLElement>>('bar');

  protected readonly feedback = signal<string | null>(null);
  protected readonly copied = signal(false);

  /** The current `{sessionId}.{rotating}` code, or null until the first window has been computed. */
  protected readonly code = signal<string | null>(null);

  /** The exact string the QR encodes and the paste field shows. */
  protected readonly payload = computed(() => {
    const code = this.code();
    return code === null ? null : enrollmentPayload(this.session().host, code);
  });

  /** True once the session's backstop has passed with no renewal having replaced it. */
  protected readonly expired = signal(false);

  /**
   * One run of the draining bar: where it starts (fraction of the window left) and how long it takes (the
   * milliseconds left). Re-set at every rotation; `key` makes two identical runs distinguishable.
   */
  protected readonly drain = signal<{fromFraction: number; durationMs: number; key: number} | null>(null);

  /**
   * The single sentence a screen reader hears. Derived from the *state*, not from the rotating value, so the
   * live region fires on a state change and never on a rotation.
   */
  protected readonly liveStatus = computed(() =>
    this.expired()
      ? 'Der Einrichtungscode ist abgelaufen. Bitte lassen Sie einen neuen anzeigen.'
      : 'Ein Einrichtungscode wird angezeigt und erneuert sich automatisch, solange die Seite geöffnet ist.');

  /** Pending timers for the open session; cleared wholesale whenever the session changes or the panel dies. */
  private timers: ReturnType<typeof setTimeout>[] = [];

  /** Bumped per session so a code computed for a superseded session can never land on screen. */
  private generation = 0;

  constructor() {
    // (Re)starts on every newly opened session — the input identity changes — which is what makes both the
    // manual "Neuen Code anzeigen" and a silent renewal restart the rotation rather than continue the old one.
    effect(() => {
      const session = this.session();
      untracked(() => this.start(session));
    });

    // The bar is driven through the Web Animations API rather than a per-second style update: one linear run
    // per window is what makes it glide. `untracked` because starting an animation reads nothing reactive.
    effect(() => {
      const run = this.drain();
      const element = this.bar()?.nativeElement;
      if (!run || !element) return;
      untracked(() => this.animateDrain(element, run.fromFraction, run.durationMs));
    });

    this.destroyRef.onDestroy(() => this.stop());
  }

  /** Wires the rotation, the renewal warning and the backstop for one session. */
  private start(session: DeviceEnrollmentSession): void {
    this.stop();
    this.generation++;
    this.expired.set(false);
    this.feedback.set(null);
    this.code.set(null);

    const expiresAt = this.plausibleExpiry(session);
    const now = Date.now();

    this.timers.push(setTimeout(() => this.renew.emit(), Math.max(0, expiresAt - now - ENROLLMENT_RENEW_LEAD_MS)));
    this.timers.push(setTimeout(() => this.lapse(), Math.max(0, expiresAt - now)));

    this.rotate(session, this.generation);
  }

  /**
   * Computes the code for the window covering now, shows it, and schedules the next rotation at the window
   * boundary. Timers are aligned to the boundary rather than run on an interval, so the code changes at the
   * same instant the server's window does — not up to a second later.
   */
  private rotate(session: DeviceEnrollmentSession, generation: number): void {
    if (generation !== this.generation || this.expired()) return;

    const now = Date.now();
    const windowSeconds = session.windowSeconds > 0 ? session.windowSeconds : DEFAULT_WINDOW_SECONDS;
    const untilNext = msUntilNextWindow(now, windowSeconds);

    this.drain.set({
      fromFraction: untilNext / (windowSeconds * 1000),
      durationMs: untilNext,
      key: windowIndexAt(now, windowSeconds)
    });

    this.codes.codeAt(session, now).then(code => {
      // A session opened while this HMAC was in flight owns the screen now; the stale result is dropped.
      if (generation === this.generation && !this.expired()) this.code.set(code);
    });

    this.timers.push(setTimeout(() => this.rotate(session, generation), untilNext));
  }

  /** The backstop passed with no renewal: stop rotating and say so. The last code stays, dimmed. */
  private lapse(): void {
    this.expired.set(true);
    this.drain.set({fromFraction: 0, durationMs: 0, key: -1});
  }

  private stop(): void {
    for (const handle of this.timers) clearTimeout(handle);
    this.timers = [];
  }

  /**
   * One linear run from `fromFraction` of the width to empty over `durationMs`. Falls back to a static width
   * where the Web Animations API is unavailable (a bar that does not move is better than a script error).
   */
  private animateDrain(element: HTMLElement, fromFraction: number, durationMs: number): void {
    const from = `${Math.max(0, Math.min(1, fromFraction)) * 100}%`;

    if (typeof element.animate !== 'function') {
      element.style.width = from;
      return;
    }

    for (const running of element.getAnimations()) running.cancel();
    element.animate([{width: from}, {width: '0%'}], {duration: Math.max(0, durationMs), easing: 'linear',
      fill: 'forwards'});
  }

  /**
   * The instant the session stops being redeemable, on this machine's clock.
   *
   * The server's `expiration` is authoritative but stamped on the server's clock. A reading that is already
   * past, or more than a day out, is replaced by the documented lifetime rather than trusted — an implausible
   * clock must not present a live session as dead.
   */
  private plausibleExpiry(session: DeviceEnrollmentSession): number {
    const now = Date.now();
    const fallback = now + ENROLLMENT_SESSION_LIFETIME_SECONDS * 1000;
    const deadline = Date.parse(session.expiration);
    if (Number.isNaN(deadline)) return fallback;

    const remaining = deadline - now;
    return remaining > 0 && remaining <= 24 * 60 * 60 * 1000 ? deadline : fallback;
  }

  /**
   * Copies the payload through the async clipboard API and, when that is unavailable or refused, **selects the
   * text and says so** rather than failing silently. A copy button that quietly does nothing is worse than no
   * button: the operator believes they have the code, navigates away, and it is gone. (Same reasoning, and the
   * same shape, as `InviteCodeDialogComponent.copy`.)
   */
  protected copyCode(): void {
    const value = this.payload();
    if (value === null) return;

    const field = this.codeField()?.nativeElement;
    const select = () => {
      field?.focus();
      field?.select();
    };

    const clipboard = navigator.clipboard;
    if (!clipboard?.writeText) {
      select();
      this.copied.set(false);
      this.feedback.set('Kopieren ist in diesem Browser nicht möglich — der Text ist markiert, bitte mit '
        + 'Strg/Cmd + C kopieren.');
      return;
    }

    clipboard.writeText(value).then(
      () => {
        this.copied.set(true);
        this.feedback.set('Code kopiert.');
      },
      () => {
        select();
        this.copied.set(false);
        this.feedback.set('Kopieren wurde vom Browser abgelehnt — der Text ist markiert, bitte mit '
          + 'Strg/Cmd + C kopieren.');
      });
  }
}
